// frontend/modules/customer-category/components/onboarding/GeneralInfoStep.tsx
import React, { useEffect } from 'react';
import {
    Form,
    Input,
    DatePicker,
    Select,
    Radio,
    Space,
    Divider,
    Typography,
    Row,
    Col
} from 'antd';
import { useTheme } from '@/components/common/ThemeProvider';
import {
    CustomerCategoryDTO,
    CustomerCategoryFieldDTO,
    CustomerOnboardingData
} from '../../types';
import { useFieldConfiguration } from '../../hooks/useFieldConfiguration';
import DynamicFormField from '../DynamicFormField';
import dayjs from 'dayjs';

const { Text } = Typography;
const { Option } = Select;

interface GeneralInfoStepProps {
    category: CustomerCategoryDTO | null;
    formData: CustomerOnboardingData;
    onUpdateFormData: (data: Partial<CustomerOnboardingData>) => void;
}

const GeneralInfoStep: React.FC<GeneralInfoStepProps> = ({
                                                             category,
                                                             formData,
                                                             onUpdateFormData
                                                         }) => {
    const { dark } = useTheme();
    const [form] = Form.useForm();

    const {
        entityFields,
        isLoading,
        fetchEntityFields
    } = useFieldConfiguration();

    // Get customer fields from category
    const customerFields = (category?.fields || []).filter(
        field => field.entityType === 'Customer' && field.isVisible
    );

    // Load entity fields if in freestyle mode
    useEffect(() => {
        if (formData.isFreestyle && !entityFields) {
            fetchEntityFields();
        }
    }, [formData.isFreestyle, entityFields, fetchEntityFields]);

    // Set initial form values from formData
    useEffect(() => {
        form.setFieldsValue(formData.customerData);
    }, [form, formData.customerData]);

    // When form values change, update parent data
    const handleValuesChange = (changedValues: any, allValues: any) => {
        onUpdateFormData({
            customerData: allValues
        });
    };

    const getFieldConfig = (fieldName: string): CustomerCategoryFieldDTO | undefined => {
        return customerFields.find(field => field.fieldName === fieldName);
    };

    const renderField = (fieldName: string, metadata: any) => {
        const fieldConfig = getFieldConfig(fieldName);
        
        return (
            <DynamicFormField
                fieldName={fieldName}
                metadata={metadata}
                fieldConfig={fieldConfig}
                category={category}
                entityType="Customer"
            />
        );
    };

    // Render fields based on category or freestyle mode
    const renderFields = () => {
        if (formData.isFreestyle && entityFields) {
            // Render all fields in freestyle mode
            return Object.entries(entityFields.customerFields).map(([fieldName, metadata]) => (
                <Col xs={24} sm={12} md={8} key={fieldName}>
                    {renderField(fieldName, metadata)}
                </Col>
            ));
        } else if (category) {
            // Render fields based on category configuration
            return customerFields
                .filter(field => field.isVisible)
                .map(field => {
                    const fieldName = field.fieldName || '';
                    const metadata = entityFields?.customerFields[fieldName] || {
                        label: field.customLabel,
                        type: 'text',
                        defaultRequired: field.isMandatory
                    };

                    return (
                        <Col xs={24} sm={12} md={8} key={fieldName}>
                            {renderField(fieldName, metadata)}
                        </Col>
                    );
                });
        }

        return null;
    };

    return (
        <div className="space-y-4">
            <Typography.Title level={5} className={dark ? 'text-white' : ''}>
                General Customer Information
            </Typography.Title>

            <Form
                form={form}
                layout="vertical"
                onValuesChange={handleValuesChange}
                initialValues={formData.customerData}
            >
                <Row gutter={[16, 0]}>
                    {renderFields()}
                </Row>
            </Form>
        </div>
    );
};

export default GeneralInfoStep;